// Localização: frontend/src/components/Navigation/Sidebar.js (0.1.4)

import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionContext';
import { useSidebar } from '../../context/SidebarContext';
import { usePlanAccess } from '../../hooks/usePlanAccess';


// Importações Lucide
import {
    LayoutDashboard,
    Users,
    CalendarCheck,
    UsersRound, // Padronizado com MobileNav
    Settings2, // Padronizado com MobileNav
    ClipboardList, // Padronizado com MobileNav (Tarefas Agendadas)
    LogOut,
    Home,
    MapPinCheck, // Padronizado com MobileNav (Check In/Out)
    NotebookPen, // Padronizado com MobileNav (Diário)
    PackageSearch, // Padronizado com MobileNav (Equipamentos)
    Pill,
    Sparkles, // Ícone para Assistente de IA
    CreditCard, // Ícone para Gerenciar Assinaturas
} from 'lucide-react';


// --------------------------------------------------------------------------------
// 1. MAPEAMENTO DE ÍCONES (Padronizado com MobileNav)
// --------------------------------------------------------------------------------
const iconMap = {
    'home': Home,
    'dashboard': LayoutDashboard,
    'pacientes': Users,
    'agendamentos': CalendarCheck,
    'ponto': MapPinCheck, // Padronizado com MobileNav
    'diario': NotebookPen, // Padronizado com MobileNav
    'gestao_pessoal': UsersRound, // Padronizado com MobileNav
    'medicamentos': Pill,
    'equipamentos': PackageSearch, // Padronizado com MobileNav
    'configuracoes': Settings2, // Padronizado com MobileNav
    'tarefas_agendadas': ClipboardList, // Padronizado com MobileNav
    'tarefas': ClipboardList, // Padronizado com MobileNav (alias)
    'assistente_ia': Sparkles, // Assistente de IA
    'admin_assinaturas': CreditCard, // Gerenciar Assinaturas (Admin)
};

// --------------------------------------------------------------------------------
// 2. DEFINIÇÃO DOS ITENS DE NAVEGAÇÃO COM RBAC
// --------------------------------------------------------------------------------
// 🛑 CORREÇÃO: Função para verificar acesso da mesma forma que PrivateRoute
const hasResourceAccess = (user, resource, checkPermission, isModuleAvailable, subscription) => {
    // Se usuário não está vinculado ou está pendente de aceitação, não tem acesso
    if (!user?.organizacao_id || user?.status === 'pendente_aceitacao' || user?.status === 'pendente_vinculacao') {
        return false;
    }

    // Roles de alto privilégio têm acesso total
    const isHighPrivilege = user?.role === 'proprietario' || user?.role === 'admin';
    if (isHighPrivilege) {
        // Verificar apenas se módulo está disponível no plano
        return isModuleAvailable(resource);
    }

    // Verificar permissões RBAC
    if (!checkPermission(resource, 'can_access')) {
        return false;
    }

    // Verificar se módulo está disponível no plano
    if (!isModuleAvailable(resource)) {
        return false;
    }

    return true;
};

// 🛑 CORREÇÃO: Função para filtrar navegação baseada em permissões reais E plano
const getNavigation = (user, checkPermission, isModuleAvailable, subscription) => {
    // Lista completa de todos os itens possíveis
    // NOTA: 'financeiro' foi removido da lista pois será renderizado como dropdown no header
    const allItems = [
        { path: '/dashboard', icon: 'dashboard', label: 'Dashboard', resource: 'dashboard' },
        { path: '/pacientes', icon: 'pacientes', label: 'Pacientes', resource: 'pacientes' },
        { path: '/agendamentos', icon: 'agendamentos', label: 'Agendamentos', resource: 'agendamentos' },
        { path: '/medicamentos', icon: 'medicamentos', label: 'Medicamentos', resource: 'medicamentos' },
        { path: '/equipamentos', icon: 'equipamentos', label: 'Equipamentos', resource: 'equipamentos' },
        { path: '/ponto', icon: 'ponto', label: 'Check In/Out', resource: 'check_in_out' },
        { path: '/diario', icon: 'diario', label: 'Diário de Cuidados', resource: 'diario' },
        { path: '/assistente-ia', icon: 'assistente_ia', label: 'Assistente de IA', resource: 'ia' },
        { path: '/usuarios', icon: 'gestao_pessoal', label: 'Gestão de Usuários', resource: 'usuarios' },
        { path: '/tarefas', icon: 'tarefas_agendadas', label: 'Gestão de Tarefas', resource: 'agenda_tarefas' },
    ];

    // ✅ NOVO: Adicionar item de Admin Assinaturas apenas para admins globais
    const adminRoles = ['global_admin', 'super_admin', 'admin'];
    if (user && adminRoles.includes(user.role)) {
        allItems.push({
            path: '/admin/assinaturas',
            icon: 'admin_assinaturas',
            label: 'Gerenciar Assinaturas',
            resource: null, // Não precisa de verificação de recurso, apenas role
            isAdminOnly: true
        });
    }

    // ✅ CORREÇÃO: Filtrar itens usando a mesma lógica do PrivateRoute
    return allItems.filter(item => {
        // Se é item admin-only, verificar apenas role
        if (item.isAdminOnly) {
            return adminRoles.includes(user?.role);
        }
        return hasResourceAccess(user, item.resource, checkPermission, isModuleAvailable, subscription);
    });
};


// --------------------------------------------------------------------------------
// 3. COMPONENTE SIDEBAR
// --------------------------------------------------------------------------------

const Sidebar = () => {
    const { user, handleLogout: logout, subscription } = useAuth();
    const { checkPermission } = usePermissions();
    const { isModuleAvailable } = usePlanAccess(); // NOVO: Verificação de plano
    const location = useLocation();
    const { isHovered, handleMouseEnter, handleMouseLeave } = useSidebar();

    // Sidebar sempre começa colapsado, expande apenas no hover
    const isCollapsed = !isHovered;

    const navigation = getNavigation(user, checkPermission, isModuleAvailable, subscription);

    // Classes de estilização para o Sidebar
    const sidebarClasses = ` 
        hidden lg:flex
        fixed left-0 top-0 h-screen 
        bg-white text-gray-800 border-r border-gray-100 
        flex flex-col justify-between flex-shrink-0 
        transition-all duration-300 ease-in-out shadow-lg 
        z-30 
        ${isCollapsed ? 'w-[80px]' : 'w-[250px]'}
    `;

    // Classes base para os itens de navegação
    const navItemBaseClasses = `
        flex items-center p-3 mx-2 rounded-lg text-gray-700
        transition-colors duration-200 border border-transparent 
    `;

    if (!user) return null;

    return (
        <div
            className="fixed left-0 top-0 h-screen z-30"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {/* Área invisível que conecta o sidebar ao texto quando colapsado */}
            {isCollapsed && (
                <div
                    className="absolute left-[80px] top-0 w-[140px] h-16 z-40"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                />
            )}

            <aside className={sidebarClasses}>

                {/* 1. Área de Cabeçalho (Logo - Sempre visível) */}
                <div
                    className={`p-4 flex items-center ${isCollapsed ? 'justify-center' : 'justify-start'}
                        border-b border-gray-100 flex-shrink-0 relative
                    `}
                >
                    {/* Logo - Sempre visível */}
                    <img
                        src="/images/higeia-icon-only.png"
                        alt="Higeia Tech Icon"
                        className="w-8 h-8 flex-shrink-0"
                    />

                    {/* Texto do logo - Posicionado absolutamente para não ser afetado pelo colapso */}
                    <span
                        className={`text-xl font-bold text-teal-800 whitespace-nowrap transition-all duration-300 ease-in-out ${isCollapsed
                                ? 'absolute left-[88px] top-1/2 -translate-y-1/2 z-50 bg-white px-2 py-1 rounded shadow-sm opacity-100 translate-x-0'
                                : 'ml-2 relative opacity-100'
                            }`}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        Higeia.tech
                    </span>
                </div>

                {/* 2. Área de Navegação */}
                <div className="flex flex-col flex-grow">
                    <nav className="mt-6 flex-grow">
                        {navigation.map((item) => {
                            const IconComponent = iconMap[item.icon];
                            const isActive = location.pathname === item.path || (location.pathname.startsWith(item.path + '/') && item.path !== '/');

                            return (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    className={`${navItemBaseClasses}
                                    ${isActive
                                            ? 'bg-teal-50 font-semibold text-teal-700 border-teal-200'
                                            : 'hover:bg-gray-50'
                                        }
                                    ${isCollapsed ? 'justify-center' : ''}
                                `}
                                    title={item.label}
                                >
                                    {IconComponent && (
                                        <IconComponent
                                            size={20}
                                            className={`${!isCollapsed && 'mr-3'} flex-shrink-0 
                                            ${isActive ? 'text-teal-600' : 'text-gray-500'}
                                        `}
                                        />
                                    )}
                                    {!isCollapsed && <span className="truncate">{item.label}</span>}
                                </NavLink>
                            );
                        })}
                    </nav>
                </div>

                {/* 3. Rodapé do Sidebar (Logout) */}
                <div className={`p-4 border-t border-gray-100 flex-shrink-0 ${isCollapsed ? 'text-center' : ''}`}>

                    {/* Botão de Sair Compacto */}
                    <button
                        onClick={logout}
                        className={`w-full text-center text-sm p-2 rounded-lg 
                        bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 transition-colors
                        flex items-center justify-center space-x-2 
                    `}
                        title="Sair da Aplicação"
                    >
                        <LogOut size={20} className="flex-shrink-0" />
                        {!isCollapsed && <span className="font-semibold">Sair</span>}
                    </button>
                </div>
            </aside>
        </div>
    );
};

export default Sidebar;